import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Dropdown,
  Row,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Form,
} from "react-bootstrap";
import { BsArrowRightShort } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

import FieldsDropdown from "../FieldsDropdown";
import {
  onChangeStripeCustomerNumberPreference,
  setIsContactSyncRulesSaved,
  onChangeStripeContactMetadataPreference,
} from "../../store/slice/contactSlice";
import { getUrl } from "../../helpers/url";

import { useCmsHook } from "../../helpers/cms/cmsHook";

function SyncRules() {
  const { cmsInstance } = useCmsHook();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  // contact states
  const {
    stripeCustomerNumberPreference,
    stripeCustomerNumberFields,
    stripeFilterPreference,
    msbcFilterPreference,
    stripeCustomerPreference,
    msbcCustomerPreference,
    msbcCustomerDropdownFields,
    stripeContactMetadataPreference,
  } = useSelector((state) => state.contact);
  // mapping states
  const {
    stripe: {
      stripeDropdownFields: {
        customerSearchFields: stripeFields,
        customer: StripeCustomerFields,
      },
      loading: stripeLoading,
    },
    msbc: {
      msbcDropdownFields: { customerSearchFields: msbcFields },
      loading: msbcLoading,
    },
  } = useSelector((state) => state.mapping);

  const [isLoading, setIsLoading] = useState(false);
  const [customerFields, setCustomerFields] = useState([]);

  /**
   * This function is used for getting the text in the dropdown
   */
  const toggleStripeCustomerNumberText = (selectedValue) => {
    return stripeCustomerNumberFields.find(
      (field) => field.value === selectedValue
    ).title;
  };

  useEffect(() => {
    if (StripeCustomerFields) {
      if (stripeCustomerNumberPreference.value === "stripe") {
        const filteredProperties = StripeCustomerFields.filter(
          (field) => field.CRF !== "doNotAdd"
        );
        setCustomerFields(filteredProperties);
      } else if (stripeCustomerNumberPreference.value === "msbc") {
        setCustomerFields(StripeCustomerFields);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [StripeCustomerFields, stripeCustomerNumberPreference.value]);

  return (
    <div>
      <hr className="settings-horizontal-line" />

      {msbcLoading || stripeLoading ? (
        <div
          className="d-flex flex-row justify-content-center align-items-center my-3"
          style={{ height: "40vh" }}
        >
          <Spinner
            animation="border"
            variant="secondary"
            role="status"
            size="md"
          ></Spinner>
        </div>
      ) : (
        <div>
          <div className="d-flex flex-column">
            {cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.contact.syncRules.section1",
                "rules-description"
              )}

            {stripeFields &&
              stripeFields.length > 0 &&
              msbcFields &&
              msbcFields.length > 0 && (
                <div>
                  <Row>
                    <Col sm={5} lg={3}>
                      <span className="rules-sub-headings">Stripe</span>
                      <FieldsDropdown
                        fieldsData={stripeFields}
                        dropdownFor={"stripeSearchParameter"}
                        selectedPreference={stripeFilterPreference}
                      />
                    </Col>
                    <Col
                      sm={1}
                      className="px-0 d-flex flex-row justify-content-center mb-1"
                      style={{ maxWidth: "40px" }}
                    >
                      <span></span>
                      <BsArrowRightShort className="mt-auto" size={25} />
                    </Col>
                    <Col sm={5} lg={3}>
                      <span className="rules-sub-headings">
                        Business Central
                      </span>
                      <FieldsDropdown
                        fieldsData={msbcFields}
                        dropdownFor={"msbcSearchParameter"}
                        selectedPreference={msbcFilterPreference}
                      />
                    </Col>
                  </Row>

                  {stripeFilterPreference &&
                    stripeFilterPreference.value === "key" && (
                      <Col sm={5} lg={2} className="mt-4">
                        <Form.Control
                          type="text"
                          placeholder="Metadata"
                          className="meta-data-text-box"
                          value={stripeContactMetadataPreference}
                          onChange={(e) =>
                            dispatch(
                              onChangeStripeContactMetadataPreference({
                                enteredInput: e.target.value,
                              })
                            )
                          }
                        />
                      </Col>
                    )}
                </div>
              )}
          </div>

          <hr className="settings-horizontal-line" />

          <div>
            {cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.contact.syncRules.section2.text",
                "rules-description"
              )}

            <Col sm={6} lg={3}>
              <Dropdown className="my-2">
                <Dropdown.Toggle className="setup-fields-dropdown">
                  {toggleStripeCustomerNumberText(
                    stripeCustomerNumberPreference.value
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  renderOnMount={true}
                  popperConfig={{ strategy: "fixed" }}
                  className="setup-fields-dropdown-items-container"
                >
                  {stripeCustomerNumberFields &&
                    stripeCustomerNumberFields.map((field) => (
                      <Dropdown.Item
                        selected
                        key={field.value}
                        onClick={() =>
                          dispatch(
                            onChangeStripeCustomerNumberPreference({
                              selectedOption: field.value,
                            })
                          )
                        }
                      >
                        {field.title}
                      </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            {stripeCustomerNumberPreference?.value === "msbc" &&
              cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.contact.syncRules.section2.renderText.createCustomer",
                "oauth-card-para"
              )}
            {stripeCustomerNumberPreference?.value === "stripe" &&
              cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.contact.syncRules.section2.renderText.takeCustomer",
                "oauth-card-para"
              )}
          </div>

          <hr className="settings-horizontal-line" />

          <div>
            {stripeCustomerNumberPreference?.value === "msbc" &&
              cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.contact.syncRules.section3.createCustomer",
                "rules-description"
              )}
            {stripeCustomerNumberPreference?.value === "stripe" &&
              cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.contact.syncRules.section3.takeCustomer",
                "rules-description"
              )}
            {StripeCustomerFields &&
              StripeCustomerFields.length > 0 &&
              msbcFields &&
              msbcFields.length > 0 && (
                <Row>
                  <Col sm={5} lg={3}>
                    <span className="rules-sub-headings">Stripe</span>
                    <FieldsDropdown
                      fieldsData={customerFields}
                      dropdownFor={"stripeCustomerField"}
                      selectedPreference={stripeCustomerPreference}
                    />
                  </Col>
                  <Col
                    sm={1}
                    className="px-0 d-flex flex-row justify-content-center mb-1"
                    style={{ maxWidth: "40px" }}
                  >
                    <span></span>
                    <BsArrowRightShort
                      className="mt-auto"
                      size={25}
                      style={{
                        transform:
                          stripeCustomerNumberPreference?.value === "msbc"
                            ? "scaleX(-1)"
                            : "",
                      }}
                    />
                  </Col>
                  <Col sm={5} lg={3}>
                    <span className="rules-sub-headings">Business Central</span>
                    <FieldsDropdown
                      fieldsData={msbcCustomerDropdownFields}
                      dropdownFor={"msbcCustomerField"}
                      selectedPreference={msbcCustomerPreference}
                      isDisabled={true}
                    />
                  </Col>
                </Row>
              )}
          </div>

          <div className="save-button-container mt-5">
            {!stripeFilterPreference ||
            !msbcFilterPreference ||
            !stripeCustomerPreference ||
            !msbcCustomerPreference ||
            (stripeFilterPreference &&
              stripeFilterPreference.value === "key" &&
              !stripeContactMetadataPreference) ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip style={{ fontSize: "11px" }}>
                    {showToolTipText(
                      stripeFilterPreference,
                      msbcFilterPreference,
                      stripeCustomerPreference,
                      msbcCustomerPreference,
                      stripeContactMetadataPreference
                    )}
                  </Tooltip>
                }
              >
                <Button
                  className="save-changes-button"
                  style={{ opacity: "0.9", cursor: "not-allowed" }}
                >
                  Save
                </Button>
              </OverlayTrigger>
            ) : (
              <Button
                className="save-changes-button"
                disabled={isLoading}
                onClick={() =>
                  onSaveContactSyncRules({
                    setIsLoading,
                    searchParams,
                    stripeFilterPreference,
                    msbcFilterPreference,
                    stripeContactMetadataPreference,
                    stripeCustomerNumberPreference,
                    stripeCustomerPreference,
                    dispatch,
                    cmsInstance,
                  })
                }
              >
                {isLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <span>Save</span>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SyncRules;

/**
 * This function is used for saving the contact sync rules
 */
export async function onSaveContactSyncRules({
  setIsLoading,
  searchParams,
  stripeFilterPreference,
  msbcFilterPreference,
  stripeContactMetadataPreference,
  stripeCustomerNumberPreference,
  stripeCustomerPreference,
  dispatch,
  cmsInstance,
}) {
  try {
    setIsLoading(true);
    const account = searchParams.get("account");
    await axios.post(getUrl("REACT_APP_SAVE_PREFERENCES"), {
      account,
      preferences: {
        filterOptions: {
          filterStripeProperty:
            stripeFilterPreference.value === "key"
              ? {
                  name: stripeFilterPreference.name,
                  value: stripeContactMetadataPreference,
                }
              : stripeFilterPreference,
          filterMsbcProperty: msbcFilterPreference,
        },
        stripeCustomerNumber: {
          direction: stripeCustomerNumberPreference.value,
          mapping: {
            stripeProperty: stripeCustomerPreference,
          },
        },
      },
      preferenceType: "customer",
    });
    dispatch(setIsContactSyncRulesSaved());
    toast.success(
      cmsInstance &&
        cmsInstance.sendData().cmsContent.notifications.syncSettings
          .successContent.contactSyncRules.message
    );
  } catch (error) {
    console.log(error);
    let errorMessage;
    if (error.response && error.response.data.message)
      errorMessage = error.response.data.message;
    else errorMessage = error.message;
    toast.error(errorMessage);
  } finally {
    setIsLoading(false);
  }
}

/**
 * This function is used for showing a custom message in Tooltip for Save button
 * @param {Object} stripeFilterPreference Filter property for Stripe
 * @param {Object} msbcFilterPreference Filter property for MSBC
 * @param {Object} stripeCustomerPreference Customer Id preference for Stripe
 * @param {Object} msbcCustomerPreference Customer id preference for MSBC
 * @returns {String} custom message
 */
const showToolTipText = (
  stripeFilterPreference,
  msbcFilterPreference,
  stripeCustomerPreference,
  msbcCustomerPreference,
  stripeContactMetadataPreference
) => {
  if (!stripeFilterPreference) {
    return "The search parameter for Stripe is not selected. Please select it before proceeding.";
  } else if (!msbcFilterPreference) {
    return "The search parameter for Business Central is not selected. Please select it before proceeding.";
  } else if (!stripeCustomerPreference) {
    return "The customer id for Stripe is not selected. Please select it before proceeding.";
  } else if (!msbcCustomerPreference) {
    return "The customer id for Business Central is not selected. Please select it before proceeding.";
  } else if (
    stripeFilterPreference &&
    stripeFilterPreference.value === "key" &&
    !stripeContactMetadataPreference
  ) {
    return "Metadata is empty. Please enter a valid metadata to proceed further.";
  }
};
